<template>
  <div id="chart" class="chart-wrapper">
      <div class="toolbar">
          <button @click="updateData(100)"  :class="{active: selection==='100'}">100</button>
          <button @click="updateData(500)"  :class="{active: selection==='500'}">500</button>
          <button @click="updateData(1000)" :class="{active: selection==='1000'}">1000</button>
      </div>

      <apexchart type="bar" height="90%" ref="chart" :options="regularsOptions" :series="regularsDensity"></apexchart>

      <p class="align-center mt-10">PMP: {{ kpis.portfolioPMPRegulars }} | TotalQty: {{ kpis.portfolioVolumeRegulars }} | Open P/L: {{ kpisDataCalcs.plRegulars.toFixed(2) }} | Allocated Capital: $ {{ kpisDataCalcs.allocatedCapitalRegulars.toFixed(2) || 0 }}</p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { densityUnitsOptions } from '@/utils/globalApexConfig';
import { getPriceBin, getBinKey } from '@/utils/binning.js';
import { calcPL } from '@/utils/funcs.js';
import Big from 'big.js';

export default {
  name: 'regularDensity',
  data() {
    return {
      interval: 500,
      selection: '500',
    }
  },
  computed: {
    ...mapState({ kpis: state => state.kpis, marketData: state => state.marketData }),
    ...mapGetters(['regularCostByBin', 'kpisDataCalcs']),

    // ─── Tier 1: cached until ledger or interval changes ─────────────────────
    costStructure() {
      return this.regularCostByBin(this.interval);
    },

    // ─── Tier 2: only the P/L loop re-runs on price ticks ────────────────────
    densityData() {
      const cs = this.costStructure;
      if (!cs) return [];

      const { startBin, steps, costMap, units } = cs;
      const marketPrice = Number(this.marketData.btcUsd ?? 0);

      const plMap = new Map();
      units.forEach(u => {
        const avgPrice = Number(u.current?.avgPrice ?? 0);
        const qty      = Number(u.current?.qty ?? 0);
        if (Number.isNaN(avgPrice) || Number.isNaN(qty)) return;
        const key = getBinKey(getPriceBin(avgPrice, this.interval));
        const pl  = new Big(calcPL(marketPrice, avgPrice, qty));
        plMap.set(key, (plMap.get(key) || new Big(0)).plus(pl));
      });

      const result = [];
      for (let i = 0; i <= steps; i++) {
        const binNum = parseFloat((startBin + i * this.interval).toFixed(8));
        const key    = getBinKey(binNum);
        result.push({
          x:  binNum.toFixed(2),
          y:  parseFloat(new Big(costMap.get(key) || 0).div(100).toFixed(2)),
          y2: parseFloat((plMap.get(key) || new Big(0)).toFixed(2)),
        });
      }
      return result;
    },

    regularsDensity() {
      const data   = this.densityData;

      return [
        {
          name: 'Regulars',
          data: data.map(p => p.y),
          type: 'bar',
          labels: data.map(p => p.x)
        },
        {
          name: 'P/L',
          type: 'line',
          data: data.map(p => p.y2),
        }
      ];
    },

    regularsOptions() {
      return {
        ...densityUnitsOptions,
        xaxis: {
          type: 'category',
          categories: this.regularsDensity[0].labels
        },
        yaxis: [
          {
            title: { text: 'Quantity' },
            show: false,
          },
          {
            title: { text: 'Open P/L' },
            opposite: true,
            show: false,
            max: 1, //nao precisa de max muito amplo pois a medida que vai acumulando lucro ja vende. O que precisa mesmo é medir o prejuizo.
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
        }
      };
    },
  },
  methods: {
    updateData(interval) {
      this.selection = `${interval}`;
      this.interval = interval;
    }
  }
}
</script>

<style scoped>
.active {
    background-color: #333 !important;
    color: #fff !important;
}

.toolbar button {
  background-color: aliceblue;
  width: 30px;
  height: 20px;
  color: #333;
  margin-right: 4px;
  font-size: 8px !important;
  padding: 0 4px !important;
}
</style>
